import { Injectable, inject } from '@angular/core';
import { Analytics, logEvent, setUserProperties, setUserId } from '@angular/fire/analytics';
import { ConsentService } from './consent.service';
import { environment } from '../../../environments/environment';

// Declare gtag for Google Consent Mode v2
declare global {
    interface Window {
        gtag?: (...args: any[]) => void;
    }
}

/**
 * GA4 silently truncates every event parameter value at 100 characters. Cutting
 * to the same length here keeps what reaches the console predictable instead of
 * looking intact in the source and arriving clipped.
 */
export const GA4_PARAM_MAX_LENGTH = 100;

/** GA4 caps an event at 25 parameters. */
const GA4_MAX_PARAMS = 25;

@Injectable({
    providedIn: 'root'
})
export class AnalyticsService {
    private analytics = inject(Analytics);
    private consentService = inject(ConsentService);
    private isProduction = environment.production;
    private debugModeEnabled = false;
    private analyticsEnabled = false;

    constructor() {
        this.checkDebugMode();
        this.subscribeToConsentChanges();
    }

    /**
     * Check if debug_mode=true URL parameter is present (dev only)
     */
    private checkDebugMode(): void {
        if (typeof window === 'undefined') {
            return;
        }

        const urlParams = new URLSearchParams(window.location.search);
        this.debugModeEnabled = urlParams.get('debug_mode') === 'true';

        // Analytics enabled in production OR in dev with debug_mode=true
        this.analyticsEnabled = this.isProduction || this.debugModeEnabled;

        if (!this.isProduction && this.debugModeEnabled && window.gtag) {
            // Enable GA4 debug mode for DebugView
            window.gtag('config', environment.firebaseConfig.measurementId, {
                'debug_mode': true
            });
            console.log('[Analytics] Debug mode enabled - events will appear in GA4 DebugView');
        } else if (!this.isProduction && !this.debugModeEnabled) {
            console.log('[Analytics] Debug mode disabled - add ?debug_mode=true to enable analytics in development');
        }
    }

    /**
     * Subscribe to consent changes and update analytics accordingly.
     *
     * The `consent default` (denied) state is set by an inline script in
     * index.html, which has to run before the Firebase SDK injects gtag.js.
     * Everything from here on is an update to that baseline.
     */
    private subscribeToConsentChanges(): void {
        this.consentService.consent$.subscribe(consent => {
            if (consent === 'granted') {
                this.updateConsent(true);
            } else if (consent === 'denied') {
                this.updateConsent(false);
            }
        });
    }

    /**
     * Push a consent update to gtag. Runs regardless of `analyticsEnabled` -
     * honouring a consent decision is a compliance obligation, not telemetry.
     */
    private updateConsent(granted: boolean): void {
        if (typeof window === 'undefined' || !window.gtag) {
            return;
        }

        window.gtag('consent', 'update', {
            // Ads consent stays denied in both directions: Echo runs no ad
            // products, so there is nothing to grant it for.
            'analytics_storage': granted ? 'granted' : 'denied',
            'ad_storage': 'denied',
            'ad_user_data': 'denied',
            'ad_personalization': 'denied'
        });

        if (!this.isProduction) {
            console.log(`[Analytics] Consent ${granted ? 'granted' : 'denied'}`);
        }
    }

    /**
     * Clip a single parameter value to GA4's limit. Non-strings pass through -
     * numbers and booleans are not length-limited.
     */
    private clip(value: any): any {
        if (typeof value !== 'string' || value.length <= GA4_PARAM_MAX_LENGTH) {
            return value;
        }
        return value.substring(0, GA4_PARAM_MAX_LENGTH - 1) + '…';
    }

    /**
     * Drop undefined values, clip strings, and enforce GA4's parameter ceiling.
     */
    private sanitize(params?: { [key: string]: any }): { [key: string]: any } | undefined {
        if (!params) {
            return undefined;
        }

        const out: { [key: string]: any } = {};
        let count = 0;

        for (const [key, value] of Object.entries(params)) {
            if (value === undefined || value === null) {
                continue;
            }
            if (count >= GA4_MAX_PARAMS) {
                if (!this.isProduction) {
                    console.warn(`[Analytics] Dropping param "${key}" - over GA4's ${GA4_MAX_PARAMS} parameter limit`);
                }
                break;
            }
            out[key] = this.clip(value);
            count++;
        }

        return out;
    }

    /**
     * Stable short hash for an error, so repeat occurrences group together in
     * GA4 even though the full stack cannot survive the 100-character limit.
     */
    fingerprint(...parts: (string | undefined)[]): string {
        const input = parts.filter(Boolean).join('|');
        // djb2
        let hash = 5381;
        for (let i = 0; i < input.length; i++) {
            hash = ((hash << 5) + hash + input.charCodeAt(i)) | 0;
        }
        return (hash >>> 0).toString(36);
    }

    /**
     * Log a custom event to Firebase Analytics
     */
    logEvent(eventName: string, eventParams?: { [key: string]: any }): void {
        if (!this.analyticsEnabled) {
            return;
        }

        const params = this.sanitize(eventParams);

        if (!this.isProduction) {
            console.log('[Analytics - Dev Mode] Event:', eventName, params);
        }

        try {
            logEvent(this.analytics, eventName, params);
        } catch (error) {
            console.error('Failed to log analytics event:', error);
        }
    }

    /**
     * Log an error as an analytics event
     */
    logError(error: Error | string, context?: { [key: string]: any }): void {
        const errorMessage = typeof error === 'string' ? error : error.message;
        const errorName = typeof error === 'string' ? 'Error' : (error.name || 'Error');

        this.logEvent('error', {
            error_name: errorName,
            error_message: errorMessage,
            error_fingerprint: this.fingerprint(errorName, errorMessage?.split('\n')[0]),
            ...context
        });
    }

    /**
     * Set user properties for analytics segmentation.
     *
     * Never pass personally identifiable information here - Google prohibits
     * PII in Analytics and can delete the property's data over it. Use
     * organization and role identifiers instead of names or email addresses.
     */
    setUserProperties(properties: { [key: string]: any }): void {
        if (!this.analyticsEnabled) {
            return;
        }

        const safe = this.sanitize(properties);

        if (!this.isProduction) {
            console.log('[Analytics - Dev Mode] User Properties:', safe);
        }

        try {
            setUserProperties(this.analytics, safe as { [key: string]: any });
        } catch (error) {
            console.error('Failed to set user properties:', error);
        }
    }

    /**
     * Set the user ID for analytics tracking
     */
    setUserId(userId: string | null): void {
        if (!this.analyticsEnabled) {
            return;
        }

        if (!this.isProduction) {
            console.log('[Analytics - Dev Mode] User ID:', userId);
        }

        try {
            setUserId(this.analytics, userId);
        } catch (error) {
            console.error('Failed to set user ID:', error);
        }
    }

    /**
     * Log a page view event
     */
    logPageView(pagePath: string, pageTitle?: string): void {
        this.logEvent('page_view', {
            page_path: pagePath,
            page_title: pageTitle || document.title
        });
    }

    /**
     * Log a button click event
     */
    logButtonClick(buttonName: string, context?: { [key: string]: any }): void {
        this.logEvent('button_click', {
            button_name: buttonName,
            ...context
        });
    }

    /**
     * Log a feature usage event
     */
    logFeatureUsage(featureName: string, action: string, context?: { [key: string]: any }): void {
        this.logEvent('feature_usage', {
            feature_name: featureName,
            action: action,
            ...context
        });
    }

    /**
     * The GA4 client ID for this browser, read from the `_ga` cookie.
     *
     * Needed when a purchase is confirmed server-side: without it, GA4 files
     * the webhook's `purchase` under its own pseudo-user and the revenue can
     * never be attributed to the session, campaign or channel that produced
     * it. Pass this into any call that ends in a server-sent event.
     *
     * Returns null before the SDK has written the cookie, or when the visitor
     * has denied analytics storage - in which case there is nothing to stitch
     * to and the server event stands alone, which is the correct outcome.
     */
    getClientId(): string | null {
        if (typeof document === 'undefined') {
            return null;
        }
        // `_ga` is written as GA1.<depth>.<clientId>, and the client ID itself
        // is the last two dot-separated parts.
        const match = document.cookie.match(/(?:^|;\s*)_ga=GA\d+\.\d+\.(\d+\.\d+)/);
        return match ? match[1] : null;
    }

    /**
     * GA4 reserved ecommerce event: the visitor has started checkout.
     *
     * GA4 builds its Monetization reports only from the reserved `purchase`,
     * `begin_checkout` and `refund` events, so the custom
     * `subscription_management` events sit alongside these rather than
     * replacing them - the custom ones drive our own funnels, these drive
     * Google's revenue reporting.
     *
     * The matching `purchase` is emitted server-side from the Paystack webhook,
     * because that is the only place a payment is known to have succeeded.
     */
    logBeginCheckout(params: {
        planName: string;
        value: number;
        currency?: string;
        billingCycle?: string;
        organizationId?: string;
    }): void {
        this.logEvent('begin_checkout', {
            currency: params.currency || 'ZAR',
            value: params.value,
            organization_id: params.organizationId,
            billing_cycle: params.billingCycle,
            items: [{
                item_id: params.planName,
                item_name: params.planName,
                item_category: 'subscription',
                price: params.value,
                quantity: 1
            }]
        });
    }
}
